import cx from "classnames";

import EditableItemGroup from "../../Utils/EditableItemGroup/EditableItemGroup";
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Flex,
  isNullish,
  Select
} from "@theorgchartdev/storybook";
import { RuleInput } from "./RuleInput";
import { appstr } from "@coreapp/utils/appstr";

/**
 * type Props = {
 *   dataTestIdPrefix?: string; (default: "rule-input-menu")
 *   editing: boolean;
 *   rule: QRule;
 *   setRule: Dispatch<SetStateAction<QRule>>
 *   ruleDivider?: boolean;
 *   showModeLabel?: boolean;
 * }
 */
export const RuleCompositeInput = props => {
  const dataTestIdPrefix = props.dataTestIdPrefix ?? "rule-input-menu";

  const selectModeNode = (
    <Flex direction="column" gap="4px">
      {props.showModeLabel && <div className="oc-rule-label">{appstr("Rule")}</div>}
      <Select
        listProps={{
          fillWidth: true,
          optionSize: "medium"
        }}
        selectSize="medium"
        {...props.modeOptions}
        id={`${dataTestIdPrefix}-mode`}
      />
    </Flex>
  );

  const actionNode = props.editingActions ? (
    <Flex justifyContent="flex-end" gap="12px">
      {props.editingActions.map(action => (
        <div key={action.label} style={{ width: "88px" }}>
          <Button
            isFullWidth
            size="small"
            modifier={action.modifier}
            onClick={action.onClick}
            disabled={action.disabled}
            dataTestId={`${dataTestIdPrefix}-${action.label}`}
          >
            {action.label}
          </Button>
        </div>
      ))}
    </Flex>
  ) : null;

  if (isNullish(props.rule)) {
    return (
      <Flex direction="column" gap="12px" width="100%">
        {props.editing ? (
          <>
            {selectModeNode}
            {actionNode}
          </>
        ) : (
          <EditableItemGroup actions={props.hoverActions || []} spaceBetween>
            <span
              className={cx("oc-text-small", {
                "ocn-builder-selectable-label": !isNullish(props.onStartEdit)
              })}
              tabIndex={0}
              role="button"
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  props.onStartEdit(e);
                }
              }}
              onClick={props.onStartEdit}
              aria-label="Would you like to add filters to narrow the focus of your chart?"
            >
              {appstr("No filters have been added")}
            </span>
          </EditableItemGroup>
        )}
      </Flex>
    );
  }

  const handleIncludeFilteredChange = () => {
    const rule = props.rule.Clone();
    const includeFiltered = rule.GetIncludeFiltered();
    const nextIncludeFiltered = !includeFiltered;
    rule.SetIncludeFiltered(nextIncludeFiltered);
    props.setRule(rule);
  };

  return (
    <EditableItemGroup actions={props.hoverActions || []}>
      <Flex direction="column" gap="12px" width="100%">
        {props.editing && selectModeNode}
        {props.ruleDivider && <Divider />}
        <RuleInput
          dataTestIdPrefix={dataTestIdPrefix}
          allowHierarchyFields={props.allowHierarchyFields}
          editing={props.editing}
          rule={props.rule}
          setRule={props.setRule}
          chartData={props.chartData}
          {...props.ruleInputProps}
        />
        <Checkbox
          id="includefiltered"
          disabled={!props.editing}
          label={appstr("includefiltered")}
          size="small"
          checked={props.rule.GetIncludeFiltered()}
          onToggle={handleIncludeFilteredChange}
        />
        {props.warning && (
          <Alert
            alertActions={{ closeAction: undefined }}
            severity="warning"
            showIcon
            className={cx("oc-padding-small", "oc-text-small")}
          >
            {props.warning}
          </Alert>
        )}
        {props.editing && actionNode}
      </Flex>
    </EditableItemGroup>
  );
};
